import React, { useEffect, useState, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage"; // Added Firebase Storage imports
import Spinner from "../components/Spinner";
import Header from "../components/Header"; // Assuming Header is generic
import Footer from "../components/Footer"; 
import { FaUserEdit, FaFileAlt, FaBriefcase, FaGraduationCap } from 'react-icons/fa'; 

const roleDisplayNames = {
  therapist: "Therapist",
  special_educator: "Special Educator",
  psychologist: "Psychologist",
  shadow_teacher: "Shadow Teacher",
  counselor: "Counselor",
  doctor: "Doctor",
  parent: "Parent", 
  cdc: "Center/CDC",
  inclusive_school: "Inclusive School",
  mainstream_school: "Mainstream School",
  corporate: "Corporate",
};

const expertiseOptions = [
  "Speech Therapist",
  "Occupational Therapist",
  "Behavioral Therapist",
  "Psychologist",
  "Special Educator",
  "Counselor",
  "Remedial Tutor",
  "Shadow Teacher",
  "Audiologist",
  "Pediatrician",
  "Other"
];

function ProfessionalProfile() {
  const [authUser, setAuthUser] = useState(null); 
  const [profile, setProfile] = useState({
    fullName: "",
    email: "", 
    mobile: "",
    city: "",
    localArea: "", 
    expertise: "", 
    services: [], 
    resumeUrl: "",
    education: [], 
    experience: [], 
    skills: [], 
    yearsExp: "", 
    role: "", 
    photoURL: "", 
    firstName: "", 
    newResumeFile: null, // Initialize newResumeFile
  });
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false); 
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [error, setError] = useState("");

  const fetchProfile = useCallback(async (user) => {
    setLoading(true);
    setError("");
    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      let userRole = "therapist"; 
      let userSpecificData = {};

      if (userDocSnap.exists()) {
        const userDataFromUsersCollection = userDocSnap.data();
        userRole = userDataFromUsersCollection.role || userRole;
        userSpecificData = { ...userSpecificData, ...userDataFromUsersCollection };
      }

      const professionalDocRef = doc(db, "professionals", user.uid);
      const professionalDocSnap = await getDoc(professionalDocRef);

      if (professionalDocSnap.exists()) {
        userSpecificData = professionalDocSnap.data();
      }
      
      // Ensure education and experience are arrays and have at least two empty slots if needed for the form
      let eduData = Array.isArray(userSpecificData.education) ? userSpecificData.education.slice(0, 2) : [];
      while (eduData.length < 2) {
        eduData.push({ type: "", degreeName: "", college: "", city: "" });
      }

      let expData = Array.isArray(userSpecificData.experience) ? userSpecificData.experience.slice(0, 2) : [];
      while (expData.length < 2) {
        expData.push({ organization: "", role: "", duration: "", description: "" });
      }

      setProfile({
        fullName: userSpecificData.fullName || user.displayName || "",
        email: user.email || "", // Always from auth
        mobile: userSpecificData.mobile || "",
        city: userSpecificData.city || "",
        localArea: userSpecificData.localArea || "",
        expertise: userSpecificData.expertise || "", 
        services: Array.isArray(userSpecificData.services) ? userSpecificData.services : (userSpecificData.services ? String(userSpecificData.services).split(',').map(s=>s.trim()).filter(Boolean) : []),
        resumeUrl: userSpecificData.resumeUrl || "",
        education: eduData,
        experience: expData,
        skills: Array.isArray(userSpecificData.skills) ? userSpecificData.skills : [],
        yearsExp: userSpecificData.yearsExp || "",
        role: userRole, // Role from 'users' collection
        photoURL: user.photoURL || "",
        firstName: user.displayName ? user.displayName.split(' ')[0] : "User",
      });

      // If no professional data exists, switch to edit mode by default
      if (!professionalDocSnap.exists()) {
        setEditMode(true);
      }

    } catch (err) {
      console.error("Error fetching profile:", err);
      setError("Failed to load profile data. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        setAuthUser(firebaseUser);
        await fetchProfile(firebaseUser);
      } else {
        setAuthUser(null);
        setProfile(prev => ({ ...prev, email: '', photoURL: '', firstName: 'User', role: '' }));
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [fetchProfile]);

  const handleSaveProfile = async () => {
    setSaving(true);
    setError("");
    try {
      const dataToSave = {
        fullName: profile.fullName,
        mobile: profile.mobile,
        city: profile.city,
        localArea: profile.localArea,
        expertise: profile.expertise,
        services: profile.services, // Already an array from state
        education: profile.education.map(edu => ({...edu})).filter(edu => edu.degreeName || edu.college || edu.type || edu.city), // Save only non-empty entries
        experience: profile.experience.map(exp => ({...exp})).filter(exp => exp.organization || exp.role || exp.duration || exp.description), // Save only non-empty entries
        // skills and yearsExp are not part of the current form requirements for editing
      };

      const storage = getStorage();

      if (profile.newResumeFile) { 
        console.log("Uploading new resume:", profile.newResumeFile.name);
        // If there was an old resume, consider deleting it if the filename/pathing strategy doesn't overwrite
        // For simplicity, we'll assume overwriting or a new unique name strategy. If fixed names, deletion is good.
        // Example: if (profile.resumeUrl && profile.resumeUrl.startsWith('https://firebasestorage.googleapis.com')) { ... delete old ... }

        const resumeRef = ref(storage, `resumes/${authUser.uid}/${profile.newResumeFile.name}`);
        await uploadBytes(resumeRef, profile.newResumeFile);
        dataToSave.resumeUrl = await getDownloadURL(resumeRef);
        console.log("New resume uploaded. URL:", dataToSave.resumeUrl);
      }

      await setDoc(doc(db, "professionals", authUser.uid), dataToSave, { merge: true });
      
      // Update fullName in 'users' collection if it has changed
      if (authUser.displayName !== profile.fullName) {
           await setDoc(doc(db, "users", authUser.uid), { fullName: profile.fullName }, { merge: true });
           // Note: Firebase Auth displayName update is separate: await updateProfile(authUser, { displayName: profile.fullName });
      }

      setProfile(prev => ({...prev, newResumeFile: null })); // Clear staged file after successful save
      setSaving(false);
      setEditMode(false);
      setToastMessage("Profile saved successfully!");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      // Re-fetch profile to show saved data, including potentially filtered education/experience and new resume URL
      fetchProfile(authUser); // This will also clear newResumeFile from state if fetchProfile resets it
    } catch (err) {
      console.error("Error saving profile:", err);
      setSaving(false);
      setError("Failed to save profile. " + (err.message || "Please try again."));
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleServicesChange = (e) => {
    const { value } = e.target;
    setProfile(prev => ({ ...prev, services: value.split(',').map(s => s.trim()).filter(s => s) }));
  };

  const handleEducationChange = (index, field, value) => {
    const updatedEducation = profile.education.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    setProfile(prev => ({ ...prev, education: updatedEducation }));
  };

  const handleExperienceChange = (index, field, value) => {
    const updatedExperience = profile.experience.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    setProfile(prev => ({ ...prev, experience: updatedExperience }));
  };

  const handleResumeUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      console.log('Selected resume file for staging:', file.name);
      // Clear any previous resumeUrl that might be a placeholder like "File selected: ..."
      // but keep it if it's an actual URL from a previously saved profile.
      const currentResumeIsPlaceholder = profile.resumeUrl && (profile.resumeUrl.startsWith('File selected:') || profile.resumeUrl.startsWith('Pending upload:'));
      setProfile(prev => ({
         ...prev, 
         newResumeFile: file, 
         resumeUrl: currentResumeIsPlaceholder || !prev.resumeUrl ? "File selected: " + file.name : prev.resumeUrl 
        })); 
      setToastMessage("Resume selected. Save profile to upload/update.");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  };

  if (loading) {
    return <Spinner message="Loading profile..." />;
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">{error}</div>;
  }

  if (!authUser) {
    return <div className="text-center py-10">Please log in to view your profile.</div>;
  }

  const displayRole = roleDisplayNames[profile.role.toLowerCase()] || profile.role;

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 py-6 md:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white shadow-xl rounded-lg p-6 mb-8 flex flex-col sm:flex-row justify-between items-center">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Welcome, {profile.firstName}!</h1>
              {profile.role && (
                <span className="mt-1 inline-block bg-indigo-500 text-white text-sm font-semibold px-3 py-1 rounded-full shadow-md">{displayRole}</span>
              )}
            </div>
            <div className="flex items-center">
              {profile.photoURL && (
                <img src={profile.photoURL} alt="Profile" className="w-16 h-16 rounded-full shadow-lg mr-4 border-2 border-indigo-300" />
              )}
              {!editMode && (
                <button 
                  onClick={() => setEditMode(true)} 
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition duration-300 ease-in-out flex items-center"
                >
                  <FaUserEdit className="mr-2" /> Edit Profile
                </button>
              )}
            </div>
          </div>

          <div className="bg-white shadow-2xl rounded-xl p-6 md:p-8">
            {editMode ? (
              <form onSubmit={(e) => { e.preventDefault(); handleSaveProfile(); }}>
                <h2 class="text-xl font-semibold text-gray-700 mb-6">Edit Your Profile</h2>
                
                <div className="mb-4">
                  <label htmlFor="fullName" className="block text-gray-700 text-sm font-bold mb-2">Full Name</label>
                  <input id="fullName" type="text" name="fullName" value={profile.fullName} onChange={handleInputChange} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" />
                </div>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-2">Email (Read-only)</label>
                  <input id="email" type="email" value={profile.email} readOnly className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight bg-gray-100 focus:outline-none focus:shadow-outline" />
                </div>
                <div className="mb-4">
                  <label htmlFor="mobile" className="block text-gray-700 text-sm font-bold mb-2">Mobile</label>
                  <input id="mobile" type="tel" name="mobile" value={profile.mobile} onChange={handleInputChange} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="city" className="block text-gray-700 text-sm font-bold mb-2">City</label>
                    <input id="city" type="text" name="city" value={profile.city} onChange={handleInputChange} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" />
                  </div>
                  <div>
                    <label htmlFor="localArea" className="block text-gray-700 text-sm font-bold mb-2">Local Area / Landmark</label>
                    <input id="localArea" type="text" name="localArea" value={profile.localArea} onChange={handleInputChange} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="expertise" className="block text-gray-700 text-sm font-bold mb-2">Expertise</label>
                  <select id="expertise" name="expertise" value={profile.expertise} onChange={handleInputChange} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline">
                    <option value="" disabled>Select your expertise</option>
                    {expertiseOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="mb-4">
                  <label htmlFor="services" className="block text-gray-700 text-sm font-bold mb-2">Services Offered (comma-separated)</label>
                  <input id="services" type="text" name="services" value={profile.services.join(', ')} onChange={handleServicesChange} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" placeholder="e.g., Therapy A, Consultation B"/>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="resumeUpload" className="block text-gray-700 text-sm font-bold mb-2">Resume</label>
                  <input id="resumeUpload" type="file" accept=".pdf,.doc,.docx" onChange={handleResumeUpload} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"/>
                  {(profile.resumeUrl && !profile.newResumeFile) && 
                    <p className="text-xs text-gray-600 mt-1">Current: 
                      <a 
                        href={profile.resumeUrl.startsWith('https://') ? profile.resumeUrl : '#'}
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={`text-indigo-600 hover:underline ${!profile.resumeUrl.startsWith('https://') ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        {profile.resumeUrl.startsWith('https://') ? 'View/Download Resume' : profile.resumeUrl}
                      </a>
                    </p>
                  }
                  {profile.newResumeFile && <p className="text-xs text-green-600 mt-1">New resume to upload: {profile.newResumeFile.name}. Save profile to apply.</p>}
                  {(profile.resumeUrl && profile.resumeUrl.startsWith('File selected:')) && !profile.newResumeFile && 
                    <p className="text-xs text-gray-500 mt-1">Previously selected: {profile.resumeUrl.replace('File selected: ','')}. Choose file again if needed.</p> }
                </div>

                {/* Education Fields - Max 2 */} 
                <div className="mb-6 border-t pt-4 mt-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3">Education (Max 2 Entries)</h3>
                  {profile.education.map((edu, index) => (
                    <div key={index} className="mb-4 p-3 border rounded-md bg-gray-50">
                      <p className="text-sm font-medium text-gray-600 mb-2">Entry {index + 1}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor={`edu_type_${index}`} className="block text-xs font-medium text-gray-600">Type (e.g., Degree, Diploma)</label>
                          <input id={`edu_type_${index}`} type="text" value={edu.type || ""} onChange={e => handleEducationChange(index, 'type', e.target.value)} className="mt-1 shadow-sm appearance-none border rounded w-full py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="e.g., Bachelor's"/>
                        </div>
                        <div>
                          <label htmlFor={`edu_degreeName_${index}`} className="block text-xs font-medium text-gray-600">Degree/Program Name</label>
                          <input id={`edu_degreeName_${index}`} type="text" value={edu.degreeName || ""} onChange={e => handleEducationChange(index, 'degreeName', e.target.value)} className="mt-1 shadow-sm appearance-none border rounded w-full py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="e.g., B.Sc. Psychology"/>
                        </div>
                        <div>
                          <label htmlFor={`edu_college_${index}`} className="block text-xs font-medium text-gray-600">College/University</label>
                          <input id={`edu_college_${index}`} type="text" value={edu.college || ""} onChange={e => handleEducationChange(index, 'college', e.target.value)} className="mt-1 shadow-sm appearance-none border rounded w-full py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="Name of Institution"/>
                        </div>
                        <div>
                          <label htmlFor={`edu_city_${index}`} className="block text-xs font-medium text-gray-600">City</label>
                          <input id={`edu_city_${index}`} type="text" value={edu.city || ""} onChange={e => handleEducationChange(index, 'city', e.target.value)} className="mt-1 shadow-sm appearance-none border rounded w-full py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="City of Institution"/>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Experience Fields - Max 2 */} 
                <div className="mb-6 border-t pt-4 mt-4">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3">Experience (Max 2 Entries)</h3>
                  {profile.experience.map((exp, index) => (
                    <div key={index} className="mb-4 p-3 border rounded-md bg-gray-50">
                      <p className="text-sm font-medium text-gray-600 mb-2">Entry {index + 1}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor={`exp_organization_${index}`} className="block text-xs font-medium text-gray-600">Organization</label>
                          <input id={`exp_organization_${index}`} type="text" value={exp.organization || ""} onChange={e => handleExperienceChange(index, 'organization', e.target.value)} className="mt-1 shadow-sm appearance-none border rounded w-full py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="Company Name"/>
                        </div>
                        <div>
                          <label htmlFor={`exp_role_${index}`} className="block text-xs font-medium text-gray-600">Role/Title</label>
                          <input id={`exp_role_${index}`} type="text" value={exp.role || ""} onChange={e => handleExperienceChange(index, 'role', e.target.value)} className="mt-1 shadow-sm appearance-none border rounded w-full py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="Your Job Title"/>
                        </div>
                        <div>
                          <label htmlFor={`exp_duration_${index}`} className="block text-xs font-medium text-gray-600">Duration</label>
                          <input id={`exp_duration_${index}`} type="text" value={exp.duration || ""} onChange={e => handleExperienceChange(index, 'duration', e.target.value)} className="mt-1 shadow-sm appearance-none border rounded w-full py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="e.g., Jan 2020 - Present or 2 years"/>
                        </div>
                        <div className="md:col-span-2">
                          <label htmlFor={`exp_description_${index}`} className="block text-xs font-medium text-gray-600">Description (Optional)</label>
                          <textarea id={`exp_description_${index}`} value={exp.description || ""} onChange={e => handleExperienceChange(index, 'description', e.target.value)} rows="3" className="mt-1 shadow-sm appearance-none border rounded w-full py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="Brief description of your responsibilities and achievements"></textarea>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end gap-4 mt-8">
                  <button type="button" onClick={() => { setEditMode(false); fetchProfile(authUser); /* Re-fetch to discard changes */ }} className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving} className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 disabled:opacity-50">
                    {saving ? <Spinner size="sm" /> : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  <div><strong className="text-gray-600">Full Name:</strong> <span className="text-gray-800">{profile.fullName || 'N/A'}</span></div>
                  <div><strong className="text-gray-600">Email:</strong> <span className="text-gray-800 bg-gray-100 px-2 py-0.5 rounded">{profile.email}</span></div>
                  <div><strong className="text-gray-600">Mobile:</strong> <span className="text-gray-800">{profile.mobile || 'N/A'}</span></div>
                  <div><strong className="text-gray-600">City:</strong> <span className="text-gray-800">{profile.city || 'N/A'}</span></div>
                  <div><strong className="text-gray-600">Local Area/Landmark:</strong> <span className="text-gray-800">{profile.localArea || 'N/A'}</span></div>
                  <div><strong className="text-gray-600">Expertise:</strong> <span className="text-gray-800">{profile.expertise || 'N/A'}</span></div>
                </div>

                <div>
                  <strong className="text-gray-600 block mb-1">Services Offered:</strong>
                  {profile.services && profile.services.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.services.map((service, index) => (
                        <span key={index} className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm shadow-sm">{service}</span>
                      ))}
                    </div>
                  ) : <span className="text-gray-500">Not specified</span>}
                </div>

                <div>
                  <strong className="text-gray-600 flex items-center mb-1"><FaFileAlt className="mr-2 text-indigo-500"/>Resume:</strong>
                  {profile.resumeUrl ? (
                    <a href={profile.resumeUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline font-medium transition duration-300">View/Download Resume</a>
                  ) : <span className="text-gray-500">Not Uploaded</span>}
                </div>

                <div>
                  <strong className="text-gray-600 block mb-2 flex items-center"><FaGraduationCap className="mr-2 text-indigo-500"/>Education (Max 2):</strong>
                  {profile.education && profile.education.length > 0 ? (
                    <ul className="space-y-3 list-inside">
                      {profile.education.map((edu, index) => (
                        <li key={index} className="bg-gray-50 p-3 rounded-lg shadow-sm border border-gray-200">
                          <p className="font-semibold text-indigo-700">{edu.degreeName || 'Degree'} {edu.type ? `(${edu.type})` : ''}</p>
                          <p className="text-sm text-gray-600">{edu.college || 'College/University'}</p>
                          <p className="text-sm text-gray-500">{edu.city || 'City'}</p>
                        </li>
                      ))}
                    </ul>
                  ) : <span className="text-gray-500">No education details provided.</span>}
                </div>

                <div>
                  <strong className="text-gray-600 block mb-2 flex items-center"><FaBriefcase className="mr-2 text-indigo-500"/>Experience (Max 2):</strong>
                  {profile.experience && profile.experience.length > 0 ? (
                    <ul className="space-y-3 list-inside">
                      {profile.experience.map((exp, index) => (
                        <li key={index} className="bg-gray-50 p-3 rounded-lg shadow-sm border border-gray-200">
                          <p className="font-semibold text-indigo-700">{exp.role || 'Role'} at {exp.organization || 'Organization'}</p>
                          <p className="text-sm text-gray-600">Duration: {exp.duration || 'N/A'}</p>
                          {exp.description && <p className="text-sm text-gray-500 mt-1">{exp.description}</p>}
                        </li>
                      ))}
                    </ul>
                  ) : <span className="text-gray-500">No experience details provided.</span>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showToast && (
        <div className="fixed bottom-5 right-5 bg-gray-800 text-white px-6 py-3 rounded-lg shadow-lg transition-opacity duration-300">
          {toastMessage}
        </div>
      )}
      <Footer />
    </>
  );
}

export default ProfessionalProfile;
